'use client'

import { useEffect } from 'react'
import { useTrackFormAction, useTrackFormStepViewed } from '@nwwp/segment-analytics'
import { cn, DisclosureSlot } from '@nwwp/ui-shadcn'
import { getTrackingParams } from '~/utils/tracking'
import { LeadGate } from './LeadGate'
import { PickStep } from './PickStep'
import { RankStep } from './RankStep'
import { ResultsView } from './ResultsView'
import { TopStep } from './TopStep'
import { useSpendCheck } from '../_hooks/use-spend-check'
import type { Screen } from '../types'

const STEP_NUMBER = { rank: 1, pick: 2, top: 3 } as const

function Progress({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div className="mb-6">
      <div className="flex max-w-180 gap-1.5" aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <i key={n} className={cn('h-0.75 flex-1 rounded-full', n <= step ? 'bg-gray-dark' : 'bg-smoke')} />
        ))}
      </div>
      <p className="mt-2.5 text-body-xs text-neutral-darker">Step {step} of 3</p>
    </div>
  )
}

export function SpendCheckContent() {
  const sc = useSpendCheck()
  const { state, goTo } = sc
  const step = state.screen in STEP_NUMBER ? STEP_NUMBER[state.screen as keyof typeof STEP_NUMBER] : null
  const trackFormStepViewed = useTrackFormStepViewed()
  const trackFormAction = useTrackFormAction()

  useEffect(() => {
    try {
      trackFormStepViewed({
        step: state.screen,
        type: 'step',
        formName: 'worthwhile-spend-check',
        ...getTrackingParams(),
      })
    } catch (error) {
      console.warn('trackFormStepViewed failed', { step: state.screen, error })
    }
  }, [state.screen, trackFormStepViewed])

  const advance = (field: string, value: string | string[], from: Screen, to: Screen) => {
    try {
      trackFormAction({
        questionName: field,
        questionValue: value,
        formName: 'worthwhile-spend-check',
        stepName: from,
      })
    } catch (error) {
      console.warn('trackFormAction failed', { field, error })
    }
    goTo(to)
  }

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-screen-md px-5 py-10 tablet:px-6 tablet:py-13">
        {step && <Progress step={step} />}

        {state.screen === 'intro' && (
          <div className="rounded-3xl bg-card p-7 shadow-sm tablet:p-13">
            <h1 className="text-title-lg text-gray-darker">
              Worthwhile Spend <em className="text-green-darker">Check</em>
            </h1>
            <p className="mt-4 max-w-130 text-body text-neutral-darker">
              Does your spending reflect what matters to you?
            </p>
            <p className="mt-4 max-w-130 text-body-sm text-neutral-darker">
              Choose the values you care about and review where your money actually went. Then, compare the difference
              between the two.
            </p>
            <button
              type="button"
              onClick={() => goTo('rank')}
              className="mt-7 rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase"
            >
              Start
            </button>
          </div>
        )}

        {state.screen === 'rank' && (
          <RankStep
            pool={state.pool}
            ranked={state.imp}
            onRank={sc.rankValue}
            onUnrank={sc.unrankValue}
            onNudge={sc.nudgeValue}
            onNext={() => advance('values', state.imp, 'rank', 'pick')}
          />
        )}

        {state.screen === 'pick' && (
          <PickStep
            ranked={state.imp}
            items={state.items}
            picked={state.picked}
            moves={state.moved}
            onToggle={sc.toggleExpense}
            onReassign={sc.reassignExpense}
            onUndo={sc.undoReassign}
            onDismiss={sc.dismissReassign}
            onAddCustom={sc.addCustomExpense}
            onBack={() => goTo('rank')}
            onNext={() => advance('expenses', Object.keys(state.picked), 'pick', 'top')}
          />
        )}

        {state.screen === 'top' && (
          <TopStep
            items={state.items}
            picked={state.picked}
            top={state.top}
            onRank={sc.rankExpense}
            onUnrank={sc.unrankExpense}
            onNudge={sc.nudgeExpense}
            onBack={() => goTo('pick')}
            onNext={() => advance('top_expenses', state.top, 'top', 'lead')}
          />
        )}

        {state.screen === 'lead' && <LeadGate state={state} onBack={() => goTo('top')} onSuccess={sc.showResults} />}

        {state.screen === 'result' && <ResultsView state={state} onRestart={sc.restart} />}

        <div className="mt-10">
          <DisclosureSlot>
            NerdWallet Wealth Partners, LLC (&ldquo;NWWP&rdquo;) is an SEC-registered investment adviser. Registration
            does not imply a certain level of skill or training. This is an educational tool provided by NWWP only and
            does not provide legal, tax, investment, or financial planning advice. Results are not recommendations and
            do not determine whether any strategy is appropriate for you. Financial planning strategies are highly
            individualized and depend on your financial circumstances, family situation, state law, and tax
            considerations. Tax laws and other assumptions used in this tool may change over time. Please consult
            qualified legal, tax, and financial professionals before implementing any strategy discussed.
          </DisclosureSlot>
        </div>
      </div>
    </div>
  )
}
